/**
 * The Markdown of node pages, read into plain data (never HTML from what people typed): the
 * Page and Play windows draw it as components, the story export turns it into escaped HTML.
 *
 * Enough of Markdown for notes and prose: headings (`#`, `##`, `###`), paragraphs (a single
 * line break stays one), lists (`-`, `*`, `1.`), quotes (`>`), rules (`---`), code blocks
 * (```), and inside a line **bold**, *italic* (or _italic_), `code` and wiki links:
 * `[[Name]]` mentions the node of that name, `[[Name|words]]` shows other words, and
 * `![[art/cover.png]]` shows one of the project's files (a picture, or a link to the file).
 */

/** A piece of a line. */
export type Inline =
  | { type: "text"; text: string }
  | { type: "strong"; children: Inline[] }
  | { type: "em"; children: Inline[] }
  | { type: "code"; text: string }
  /** A wiki link: the name it points at, and the words shown. */
  | { type: "wiki"; target: string; label: string }
  /** One of the project's files shown in the page (`![[path]]`): its path, and the words for it. */
  | { type: "embed"; target: string; label: string };

/** A block of the page. */
export type Block =
  | { type: "heading"; level: 1 | 2 | 3; inline: Inline[] }
  | { type: "paragraph"; inline: Inline[] }
  | { type: "list"; ordered: boolean; items: Inline[][] }
  | { type: "quote"; inline: Inline[] }
  | { type: "rule" }
  | { type: "code"; text: string };

/** What starts a piece of a line with a look of its own (the first that matches wins). */
const INLINE = /(!?\[\[[^\]\n]+?\]\]|\*\*[^*\n]+?\*\*|`[^`\n]+`|\*[^*\s][^*\n]*?\*|_[^_\s][^_\n]*?_)/g;

/** The pieces of one line (or a paragraph's lines). */
export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  let last = 0;
  for (const match of text.matchAll(INLINE)) {
    const at = match.index ?? 0;
    if (at > last) out.push({ type: "text", text: text.slice(last, at) });
    const piece = match[0];
    if (piece.startsWith("![[")) {
      const [target = "", label] = piece.slice(3, -2).split("|");
      out.push({ type: "embed", target: target.trim(), label: (label ?? target).trim() });
    } else if (piece.startsWith("[[")) {
      const [target = "", label] = piece.slice(2, -2).split("|");
      out.push({ type: "wiki", target: target.trim(), label: (label ?? target).trim() });
    } else if (piece.startsWith("**")) out.push({ type: "strong", children: parseInline(piece.slice(2, -2)) });
    else if (piece.startsWith("`")) out.push({ type: "code", text: piece.slice(1, -1) });
    else out.push({ type: "em", children: parseInline(piece.slice(1, -1)) });
    last = at + piece.length;
  }
  if (last < text.length) out.push({ type: "text", text: text.slice(last) });
  return out;
}

/** A list item's start: its marker, and whether it is numbered. */
function listItem(line: string): { ordered: boolean; rest: string } | null {
  const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
  if (bullet) return { ordered: false, rest: bullet[1]! };
  const number = /^\s*\d+[.)]\s+(.*)$/.exec(line);
  return number ? { ordered: true, rest: number[1]! } : null;
}

/** Whether `line` starts a block of its own (so it ends a paragraph). */
function startsBlock(line: string): boolean {
  return /^(#{1,6}\s|>|```|(-{3,}|\*{3,}|_{3,})\s*$)/.test(line.trim()) || listItem(line) !== null;
}

/** The blocks of a page. */
export function parseMarkdown(text: string): Block[] {
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    const trimmed = line.trim();
    if (!trimmed) {
      i++;
    } else if (trimmed.startsWith("```")) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !lines[i]!.trim().startsWith("```")) body.push(lines[i++]!);
      i++;
      blocks.push({ type: "code", text: body.join("\n") });
    } else if (/^#{1,6}\s/.test(trimmed)) {
      const hashes = trimmed.indexOf(" ");
      blocks.push({ type: "heading", level: Math.min(3, hashes) as 1 | 2 | 3, inline: parseInline(trimmed.slice(hashes + 1).trim()) });
      i++;
    } else if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
      blocks.push({ type: "rule" });
      i++;
    } else if (trimmed.startsWith(">")) {
      const body: string[] = [];
      while (i < lines.length && lines[i]!.trim().startsWith(">")) body.push(lines[i++]!.trim().replace(/^>\s?/, ""));
      blocks.push({ type: "quote", inline: parseInline(body.join("\n")) });
    } else if (listItem(line)) {
      const ordered = listItem(line)!.ordered;
      const items: Inline[][] = [];
      while (i < lines.length && listItem(lines[i]!)?.ordered === ordered) items.push(parseInline(listItem(lines[i++]!)!.rest));
      blocks.push({ type: "list", ordered, items });
    } else {
      const body: string[] = [];
      while (i < lines.length && lines[i]!.trim() && !(body.length && startsBlock(lines[i]!))) body.push(lines[i++]!);
      blocks.push({ type: "paragraph", inline: parseInline(body.join("\n")) });
    }
  }
  return blocks;
}

/** The names a page mentions with wiki links, each once, in the order they first appear (code blocks left out). */
export function wikiTargets(text: string): string[] {
  const found: string[] = [];
  const seen = new Set<string>();
  const visit = (pieces: Inline[]) => {
    for (const p of pieces) {
      if (p.type === "wiki" && p.target && !seen.has(p.target.toLowerCase())) {
        seen.add(p.target.toLowerCase());
        found.push(p.target);
      } else if (p.type === "strong" || p.type === "em") visit(p.children);
    }
  };
  for (const b of parseMarkdown(text)) {
    if (b.type === "list") b.items.forEach(visit);
    else if (b.type !== "rule" && b.type !== "code") visit(b.inline);
  }
  return found;
}

/** `text` with the characters HTML gives a meaning to written as entities. */
export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/** Pieces of a line as HTML; a wiki link is what `wiki` makes of it (escaped by `wiki`). */
function inlineHtml(pieces: Inline[], wiki: (target: string, label: string) => string): string {
  return pieces
    .map((p) => {
      switch (p.type) {
        case "text":
          return escapeHtml(p.text);
        case "strong":
          return `<strong>${inlineHtml(p.children, wiki)}</strong>`;
        case "em":
          return `<em>${inlineHtml(p.children, wiki)}</em>`;
        case "code":
          return `<code>${escapeHtml(p.text)}</code>`;
        case "wiki":
          return wiki(p.target, p.label);
        case "embed":
          return `<span class="file">${escapeHtml(p.label)}</span>`;
      }
    })
    .join("");
}

/** A page as HTML, everything typed escaped; `wiki` writes a wiki link (by default its words in a `<span class="mention">`). */
export function markdownHtml(text: string, wiki: (target: string, label: string) => string = (_, label) => `<span class="mention">${escapeHtml(label)}</span>`): string {
  return parseMarkdown(text)
    .map((b) => {
      switch (b.type) {
        case "heading":
          return `<h${b.level}>${inlineHtml(b.inline, wiki)}</h${b.level}>`;
        case "paragraph":
          return `<p>${inlineHtml(b.inline, wiki)}</p>`;
        case "quote":
          return `<blockquote>${inlineHtml(b.inline, wiki)}</blockquote>`;
        case "list": {
          const tag = b.ordered ? "ol" : "ul";
          return `<${tag}>${b.items.map((item) => `<li>${inlineHtml(item, wiki)}</li>`).join("")}</${tag}>`;
        }
        case "rule":
          return "<hr>";
        case "code":
          return `<pre><code>${escapeHtml(b.text)}</code></pre>`;
      }
    })
    .join("\n");
}
